import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Shield, CheckCircle, Globe, Lock, Zap, ArrowRight, CreditCard, Star, Clock, FileText, Users, Award, ShieldCheck, BarChart, Loader } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const MainContent = styled.main`
  max-width: 100%;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
  line-height: 1.2;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const HeroDescription = styled.p`
  font-size: 1rem;
  color: #4b5563;
  max-width: 42rem;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
    margin-bottom: 1.25rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const PricingCard = styled.article`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 0.75rem;
  padding: 1.75rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: #3b82f6;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(90deg, #059669 0%, #10b981 100%);
  padding: 0.375rem 1.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom-left-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    gap: 0.875rem;
    margin-bottom: 1.25rem;
  }
`;

const IconWrapper = styled.div`
  padding: 0.75rem;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  border-radius: 0.75rem;
  border: 1px solid rgba(30, 64, 175, 0.2);
  box-shadow: 0 3px 8px rgba(30, 64, 175, 0.1);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0.625rem;
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ServiceTitle = styled.h2`
  font-size: 1.375rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.375rem 0;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const ServiceSubtitle = styled.p`
  color: #6b7280;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const OriginalPriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
`;

const OriginalPrice = styled.div`
  font-size: 1rem;
  color: #6b7280;
  position: relative;
  display: inline-block;
  
  span {
    position: relative;
    padding-right: 0.25rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
    transform: translateY(-50%);
    border-radius: 1px;
  }
`;

const DiscountBadge = styled.div`
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 0.1875rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.2);
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
`;

const Price = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PriceUnit = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ScanDetails = styled.div`
  background: #f9fafb;
  border: 1px solid #f1f5f9;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ScanTitle = styled.h3`
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const ScanInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.875rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    gap: 0.625rem;
  }
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;
  }
`;

const PaymentButton = styled.button`
  width: 100%;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  font-weight: 600;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  letter-spacing: 0.025em;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(30, 64, 175, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 3px 10px rgba(30, 64, 175, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    
    &::before {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  
  @media (max-width: 1024px) {
    gap: 1rem;
  }
`;

const SidebarCard = styled.section`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    border-color: #d1d5db;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const SidebarTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.875rem;
  border-bottom: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  
  span {
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.4;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    span {
      font-size: 0.8125rem;
    }
  }
`;

const QuickFacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FactItem = styled.div`
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 1rem;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const FactLabel = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const FactValue = styled.p`
  color: #111827;
  font-weight: 600;
  font-size: 0.9375rem;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TrustSection = styled.section`
  margin-top: 3rem;
  text-align: center;
  background: #f9fafb;
  padding: 2rem 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    margin-top: 2.5rem;
    padding: 1.5rem 1rem;
  }
`;

const TrustTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const TrustText = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 1.25rem;
  }
`;

const TrustBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 1rem;
`;

const TrustBadge = styled.div`
  background: white;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #3b82f6;
  }
  
  span {
    color: #111827;
    font-weight: 600;
    font-size: 0.8125rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    
    span {
      font-size: 0.75rem;
    }
  }
`;

const Footer = styled.footer`
  margin-top: 2.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
  
  p {
    color: #6b7280;
    font-size: 0.8125rem;
    line-height: 1.5;
    max-width: 36rem;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    margin-top: 2rem;
    padding-top: 1.25rem;
    
    p {
      font-size: 0.75rem;
    }
  }
`;

const ValueProposition = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ValueCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #f1f5f9;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const ValueIcon = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  
  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    margin-bottom: 0.75rem;
  }
`;

const ValueTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const ValueDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const UpgradePlan = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [scanCount, setScanCount] = useState(1);
  const [phone, setPhone] = useState('');
  const [availableScans, setAvailableScans] = useState({
    website: 0,
    api: 0,
    network: 0,
    firewall: 0
  });
  const navigate = useNavigate();

  // Price constants
  const ORIGINAL_PRICE_PER_SCAN = 4999;
  const DISCOUNTED_PRICE_PER_SCAN = 999;
  const DISCOUNT_PERCENTAGE = Math.round((ORIGINAL_PRICE_PER_SCAN - DISCOUNTED_PRICE_PER_SCAN) / ORIGINAL_PRICE_PER_SCAN * 100);

  useEffect(() => {
    fetchAvailableScans();
  }, []);

  const fetchAvailableScans = async () => {
    try {
      const response = await apiRequest('/api/payments/available-scans/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableScans(data.available_scans);
        }
      }
    } catch (error) {
      console.error('Error fetching available scans:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const handlePayment = async () => {
    if (!phone) {
      alert('Please enter your phone number');
      return;
    }

    if (scanCount < 1 || scanCount > 100) {
      alert('Scan count must be between 1 and 100');
      return;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phone)) {
      alert('Please enter a valid 10-digit Indian phone number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiRequest('/api/payments/purchase/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scan_type: 'website',
          scan_count: scanCount,
          phone: phone
        })
      });

      if (response && response.ok) {
        const data = await response.json();
        
        if (data.success) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = data.payment_url || 'https://test.payu.in/_payment';
          
          const paymentData = data.payment_data;
          for (const key in paymentData) {
            if (paymentData.hasOwnProperty(key)) {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = paymentData[key];
              form.appendChild(input);
            }
          }

          document.body.appendChild(form);
          form.submit();
        } else {
          alert('Failed to initiate payment: ' + (data.message || 'Unknown error'));
        }
      } else {
        const errorData = response ? await response.json() : null;
        alert(`Error: ${errorData?.message || 'Failed to initiate payment'}`);
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    'Complete web application security assessment',
    'OWASP Top 10 vulnerability testing',
    'SQL injection and XSS detection',
    'Authentication and session testing',
    'Detailed vulnerability report with remediation',
    'Post-scan consultation call'
  ];

  const availableWebsiteScans = availableScans.website || 0;

  return (
    <PageContainer>
      <MainContent>
        <HeroSection>
          <HeroTitle>
            Advanced <GradientText>Security Assessment</GradientText> for Modern Web Applications
          </HeroTitle>
          
          <HeroDescription>
            Professional penetration testing service designed to identify and remediate security vulnerabilities in your web applications. Our certified security experts employ industry-standard methodologies to ensure comprehensive coverage.
          </HeroDescription>
        </HeroSection>

        <ContentGrid>
          <PricingCard>
            <RecommendedBadge>Special Discount</RecommendedBadge>
            
            <ServiceHeader>
              <IconWrapper>
                <Globe size={22} color="white" />
              </IconWrapper>
              <ServiceInfo>
                <ServiceTitle>Comprehensive Web Application Penetration Test</ServiceTitle>
                <ServiceSubtitle>
                  Complete security assessment including automated scanning, manual testing, and detailed reporting with expert consultation.
                </ServiceSubtitle>
              </ServiceInfo>
            </ServiceHeader>

            <OriginalPriceContainer>
              <OriginalPrice>
                <span>₹{ORIGINAL_PRICE_PER_SCAN}</span>
              </OriginalPrice>
              <DiscountBadge>
                Save {DISCOUNT_PERCENTAGE}%
              </DiscountBadge>
            </OriginalPriceContainer>

            <PriceSection>
              <Price>₹{DISCOUNTED_PRICE_PER_SCAN * scanCount}</Price>
              <PriceUnit>/ for {scanCount} Scan{scanCount > 1 ? 's' : ''}</PriceUnit>
            </PriceSection>

            <ScanDetails>
              <ScanTitle>
                <FileText size={18} color="#1e40af" />
                Purchase Configuration
              </ScanTitle>
              <ScanInfo>
                <InfoItem style={{ gridColumn: '1 / -1' }}>
                  <IconCircle>
                    <CheckCircle size={18} color="#10b981" />
                  </IconCircle>
                  <div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Your Current Balance:</strong>
                      <div style={{ 
                        display: 'inline-block',
                        marginLeft: '0.5rem',
                        padding: '0.1875rem 0.5rem',
                        background: availableWebsiteScans > 0 ? '#f0fdf4' : '#fef2f2',
                        border: availableWebsiteScans > 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                        borderRadius: '4px',
                        color: availableWebsiteScans > 0 ? '#065f46' : '#991b1b',
                        fontSize: '0.8125rem',
                        fontWeight: 600
                      }}>
                        {availableWebsiteScans} Website Scan{availableWebsiteScans !== 1 ? 's' : ''} Available
                      </div>
                    </div>
                    {availableWebsiteScans > 0 && (
                      <p style={{ 
                        fontSize: '0.8125rem', 
                        color: '#6b7280',
                        marginTop: '0.5rem'
                      }}>
                        You already have {availableWebsiteScans} scan{availableWebsiteScans !== 1 ? 's' : ''} available. Purchase additional scans if needed.
                      </p>
                    )}
                  </div>
                </InfoItem>

                <InfoItem>
                  <IconCircle>
                    <Clock size={18} color="#1e40af" />
                  </IconCircle>
                  <div>
                    <label htmlFor="scanCount" style={{ display: 'block', marginBottom: '0.375rem' }}>
                      <strong>Number of Additional Scans</strong>
                    </label>
                    <select
                      id="scanCount"
                      value={scanCount}
                      onChange={(e) => setScanCount(parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: '1px solid #f1f5f9',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {[1, 2, 3, 4, 5, 10, 20, 50, 100].map(num => (
                        <option key={num} value={num}>
                          {num} scan{num > 1 ? 's' : ''} - ₹{DISCOUNTED_PRICE_PER_SCAN * num}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      <span style={{ textDecoration: 'line-through', color: '#ef4444', marginRight: '0.5rem' }}>
                        ₹{ORIGINAL_PRICE_PER_SCAN}
                      </span>
                      Now: ₹{DISCOUNTED_PRICE_PER_SCAN} per scan
                    </div>
                  </div>
                </InfoItem>
                
                <InfoItem>
                  <IconCircle>
                    <Users size={18} color="#1e40af" />
                  </IconCircle>
                  <div>
                    <label htmlFor="phone" style={{ display: 'block', marginBottom: '0.375rem' }}>
                      <strong>Phone Number *</strong>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Enter 10-digit mobile number"
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: phone ? '1px solid #10b981' : '1px solid #f1f5f9',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        backgroundColor: 'white'
                      }}
                      maxLength={10}
                      required
                    />
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      Required for payment verification
                    </div>
                  </div>
                </InfoItem>
              </ScanInfo>
            </ScanDetails>

            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{
                padding: '0.875rem',
                background: '#f8fafc',
                border: '1px solid #f1f5f9',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                color: '#475569'
              }}>
                <strong>Payment Summary:</strong>
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>{scanCount} Website Scan{scanCount > 1 ? 's' : ''} @ ₹{DISCOUNTED_PRICE_PER_SCAN} each</span>
                    <span>₹{DISCOUNTED_PRICE_PER_SCAN * scanCount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.75rem', color: '#ef4444' }}>
                    <span>You save: ₹{(ORIGINAL_PRICE_PER_SCAN - DISCOUNTED_PRICE_PER_SCAN) * scanCount}</span>
                    <span style={{ textDecoration: 'line-through' }}>₹{ORIGINAL_PRICE_PER_SCAN * scanCount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9', fontWeight: 600 }}>
                    <span>Total Amount</span>
                    <span>₹{DISCOUNTED_PRICE_PER_SCAN * scanCount}</span>
                  </div>
                </div>
              </div>
            </div>

            <PaymentButton 
              onClick={handlePayment}
              disabled={isLoading || !phone || phone.length !== 10}
            >
              {isLoading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  <span>Processing Payment...</span>
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  <span>Proceed to Secure Payment</span>
                  <ArrowRight size={18} />
                </>
              )}
            </PaymentButton>

            <div style={{
              textAlign: 'center',
              marginTop: '0.875rem',
              fontSize: '0.75rem',
              color: '#6b7280'
            }}>
              <p>Limited time offer • Original price: ₹4999 per scan • Powered by PayU</p>
            </div>
          </PricingCard>

          <Sidebar>
            <SidebarCard>
              <SidebarTitle>
                <Lock size={18} color="#1e40af" />
                <span>Testing Methodology</span>
              </SidebarTitle>
              <FeatureList>
                {features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckCircle size={16} color="#10b981" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>
                <Zap size={18} color="#f59e0b" />
                <span>Special Offer Details</span>
              </SidebarTitle>
              <QuickFacts>
                <FactItem>
                  <FactLabel>Original Price</FactLabel>
                  <FactValue style={{ textDecoration: 'line-through', color: '#ef4444' }}>₹4999 per scan</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>Discounted Price</FactLabel>
                  <FactValue style={{ color: '#059669', fontWeight: '600' }}>₹999 per scan</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>You Save</FactLabel>
                  <FactValue style={{ color: '#059669', fontWeight: '600' }}>₹4000 per scan (80%)</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>Offer Validity</FactLabel>
                  <FactValue>Limited time only</FactValue>
                </FactItem>
              </QuickFacts>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>
                <Shield size={18} color="#1e40af" />
                <span>Benefits</span>
              </SidebarTitle>
              <QuickFacts>
                <FactItem>
                  <FactLabel>Instant Activation</FactLabel>
                  <FactValue>Scans available immediately</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>No Expiry</FactLabel>
                  <FactValue>Use scans anytime</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>Priority Support</FactLabel>
                  <FactValue>Dedicated assistance</FactValue>
                </FactItem>
                <FactItem>
                  <FactLabel>Bulk Discounts</FactLabel> 
                  <FactValue>Contact for &gt;50 scans</FactValue>
                </FactItem>
              </QuickFacts>
            </SidebarCard>
          </Sidebar>
        </ContentGrid>

        <ValueProposition>
          <ValueCard>
            <ValueIcon>
              <ShieldCheck size={20} color="white" />
            </ValueIcon>
            <ValueTitle>Secure Payment</ValueTitle>
            <ValueDescription>All transactions are secured with 256-bit SSL encryption</ValueDescription>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon>
              <Clock size={20} color="white" />
            </ValueIcon>
            <ValueTitle>Instant Activation</ValueTitle>
            <ValueDescription>Scans are available immediately after successful payment</ValueDescription>
          </ValueCard>
          
          <ValueCard>
            <ValueIcon>
              <Award size={20} color="white" />
            </ValueIcon>
            <ValueTitle>Limited Time Offer</ValueTitle>
            <ValueDescription>Save ₹4000 per scan on this special discount</ValueDescription>
          </ValueCard>
        </ValueProposition>

        <TrustSection>
          <TrustTitle>
            <ShieldCheck size={20} color="#1e40af" />
            100% Secure Payment Processing
          </TrustTitle>
          <TrustText>
            Your payment is processed securely by PayU, India's leading payment gateway. We never store your card details.
          </TrustText>
          <TrustBadges>
            <TrustBadge>
              <Shield size={16} color="#1e40af" />
              <span>PCI DSS Level 1</span>
            </TrustBadge>
            <TrustBadge>
              <Shield size={16} color="#1e40af" />
              <span>256-bit SSL</span>
            </TrustBadge>
            <TrustBadge>
              <Shield size={16} color="#1e40af" />
              <span>ISO 27001</span>
            </TrustBadge>
            <TrustBadge>
              <Shield size={16} color="#1e40af" />
              <span>3D Secure</span>
            </TrustBadge>
          </TrustBadges>
        </TrustSection>

        <Footer>
          <p>
            Need help? Contact support@zerodayops.com • For enterprise/bulk purchases: sales@zerodayops.com • Phone: +91-9810005685
          </p>
          <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: '#9ca3af' }}>
            Special discount offer valid for limited time only. Original price ₹4999 per scan.
          </p>
        </Footer>
      </MainContent>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </PageContainer>
  );
};

export default UpgradePlan;